import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../store/userStore";
import { fetchMastersWithStatus } from "../../apiServices/partsCatalogStatusApi";
import type { MasterStatusDto } from "../../models/MasterStatus";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./AuthoringPageCheck.css";

const PAGE_SIZE = 5;

const AuthoringPageCheck: React.FC = () => {
  const user = useUserStore((s) => s.user);
  const navigate = useNavigate();
  const role = String(user?.role || "OEM").toUpperCase();
  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `OEM${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "OEM1";

  const [partNumber, setPartNumber] = useState("");
  const [description, setDescription] = useState("");
  const [filterText, setFilterText] = useState("");
  const [items, setItems] = useState<MasterStatusDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchMastersWithStatus(oem, role);
        if (mounted) setItems(data.items || []);
      } catch (e) {
        if (mounted) setError("Failed to load parts list");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [oem, role]);

  const filtered = useMemo(() => {
    const p = partNumber.trim().toLowerCase();
    const d = description.trim().toLowerCase();
    const f = filterText.trim().toLowerCase();

    return items.filter((it) => {
      const pn = String(it.componentPartNumber || "").toLowerCase();
      const desc = String(it.componentDescription || "").toLowerCase();
      const version = String(it.currentVersionId ?? "").toLowerCase();
      const status = String(it.currentStatus || it.currentStatusId || "").toLowerCase();

      const createdBy = String(it.CreatedBy || it.createdBy || "").toLowerCase();
      const createdOnRaw = String(it.CreatedOn || it.createdOn || "").toLowerCase();
      const createdOnFormatted = createdOnRaw ? formatDate(createdOnRaw).toLowerCase() : "";

      const modifiedBy = String(it.ModifiedBy || it.modifiedBy || "").toLowerCase();
      const modifiedOnRaw = String(it.ModifiedOn || it.modifiedOn || "").toLowerCase();
      const modifiedOnFormatted = modifiedOnRaw ? formatDate(modifiedOnRaw).toLowerCase() : "";

      const blob = [
        pn,
        desc,
        version,
        status,
        createdBy,
        createdOnRaw,
        createdOnFormatted,
        modifiedBy,
        modifiedOnRaw,
        modifiedOnFormatted,
      ]
        .join(" ")
        .trim();
      if (p && !pn.includes(p)) return false;
      if (d && !desc.includes(d)) return false;
      if (f && !blob.includes(f)) return false;
      return true;
    });
  }, [items, partNumber, description, filterText]);

  useEffect(() => {
    setPage(1);
  }, [partNumber, description, filterText]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const startItemNo = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endItemNo = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="authoring-check">
      <div className="authoring-check__topbar" />

      <div className="authoring-check__panel">
        <div className="authoring-check__row">
          <button type="button" className="authoring-check__label">
            PART NUMBER
          </button>
          <div className="authoring-check__input">
            <input
              value={partNumber}
              onChange={(e) => setPartNumber(e.target.value)}
              placeholder="PART NUMBER"
            />
            <button
              type="button"
              className="authoring-check__clear"
              onClick={() => setPartNumber("")}
              aria-label="Clear part number"
            >
              <ClearIcon />
            </button>
          </div>
        </div>

        <div className="authoring-check__row">
          <button type="button" className="authoring-check__label">
            DESCRIPTION
          </button>
          <div className="authoring-check__input">
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="DESCRIPTION"
            />
            <button
              type="button"
              className="authoring-check__clear"
              onClick={() => setDescription("")}
              aria-label="Clear description"
            >
              <ClearIcon />
            </button>
          </div>
        </div>

        <div className="authoring-check__row authoring-check__row--actions">
          <div className="authoring-check__actions">
            {/* <button type="button" className="authoring-check__action">
              SEARCH
            </button> */}
                  <button
                    type="button"
                    className="authoring-check__action"
                    onClick={() => navigate("/authoring/createpartnumber")}
                  >
                    CREATE NEW
                  </button>
          </div>
        </div>

        <div className="authoring-check__row authoring-check__row--filter">
          <div className="authoring-check__input authoring-check__input--wide">
            <input
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="TYPE THE TEXT TO FILTER"
            />
            <button
              type="button"
              className="authoring-check__clear"
              onClick={() => setFilterText("")}
              aria-label="Clear filter"
            >
              <ClearIcon />
            </button>
          </div>
        </div>
      </div>

      <div className="authoring-check__table">
        <div className="authoring-check__thead">
          <div>ACTIONS</div>
          <div>PARTNUMBER</div>
          <div>DESCRIPTION</div>
          <div>VER</div>
          <div>STATUS</div>
          <div>CREATED BY</div>
          <div>CR DT</div>
          <div>MODIFIED BY</div>
          <div>MD DT</div>
        </div>

        {loading && <div className="authoring-check__empty">Loading...</div>}
        {error && <div className="authoring-check__empty">{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="authoring-check__empty">No results</div>
        )}

        {pageItems.map((it) => {
          const createdBy = it.CreatedBy || it.createdBy || "-";
          const createdOn = it.CreatedOn || it.createdOn || null;
          const modifiedBy = it.ModifiedBy || it.modifiedBy || "-";
          const modifiedOn = it.ModifiedOn || it.modifiedOn || null;

          return (
            <div className="authoring-check__rowitem" key={it.masterAssemblyId}>
              <div className="authoring-check__actions-cell">
                <button
                  type="button"
                  className="authoring-check__icon"
                  onClick={() => navigate(`/authoring/edit/${it.masterAssemblyId}`)}
                >
                  EDIT
                </button>
                <button
                  type="button"
                  className="authoring-check__icon"
                  onClick={() => navigate(`/authoring/view/${it.masterAssemblyId}`)}
                >
                  VIEW
                </button>
              </div>
              <div>{it.componentPartNumber || "-"}</div>
              <div>{it.componentDescription || "-"}</div>
              <div>{it.currentVersionId ?? "-"}</div>
              <div>{it.currentStatus || it.currentStatusId || "-"}</div>
              <div>{createdBy}</div>
              <div>{createdOn ? formatDate(createdOn) : "-"}</div>
              <div>{modifiedBy}</div>
              <div>{modifiedOn ? formatDate(modifiedOn) : "-"}</div>
            </div>
          );
        })}

        {!loading && !error && filtered.length > 0 && (
          <div className="authoring-check__pagination">
            <div className="authoring-check__pageinfo">
              Showing {startItemNo} to {endItemNo} of {filtered.length}
            </div>
            <div className="authoring-check__pager">
              <button
                type="button"
                className="authoring-check__pagebtn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Prev
              </button>
              <span className="authoring-check__pageno">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="authoring-check__pagebtn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const year = String(d.getFullYear()).slice(-2);
  return `${day} ${month} ${year}`;
};

const ClearIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.4993 7.49935L7.49935 12.4993M7.49935 7.49935L12.4993 12.4993M2.15435 13.9377C1.84177 13.6252 1.66611 13.2013 1.66602 12.7593V7.23935C1.66611 6.79736 1.84177 6.3735 2.15435 6.06102L6.06102 2.15435C6.3735 1.84177 6.79736 1.66611 7.23935 1.66602H12.7593C13.2013 1.66611 13.6252 1.84177 13.9377 2.15435L17.8443 6.06102C18.1569 6.3735 18.3326 6.79736 18.3327 7.23935V12.7593C18.3326 13.2013 18.1569 13.6252 17.8443 13.9377L13.9377 17.8443C13.6252 18.1569 13.2013 18.3326 12.7593 18.3327H7.23935C6.79736 18.3326 6.3735 18.1569 6.06102 17.8443L2.15435 13.9377Z"
      stroke="#4D4D4D"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default AuthoringPageCheck;
